import { create } from 'zustand';
import { User } from '../types/index.js';
import { db } from '../db/index.js';
import { startGuestClaim } from '../db/claim.js';
import { apiClient } from '../utils/apiClient.js';

/** FRE-108: stamp unowned sessions with the user who is leaving, so the next account cannot adopt them. */
const releaseSessionsTo = (userId: string | undefined): void => {
  if (!userId || userId.startsWith('guest-')) return;
  void db.sessions
    .filter((s) => !s.userId)
    .modify({ userId })
    .catch(() => {});
};

/** FND-02: same as releaseSessionsTo, for unowned routines (the shared device sample stays unowned). */
const releaseRoutinesTo = (userId: string | undefined): void => {
  if (!userId || userId.startsWith('guest-')) return;
  void db.routines
    .filter((r) => !r.ownerId && !r.isSample)
    .modify({ ownerId: userId })
    .catch(() => {});
};

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isGuest: boolean;
  isLoading: boolean;
  /** FRE-137: the email-verification dialog is global so any 403 EMAIL_NOT_VERIFIED can open it. */
  verifyDialogOpen: boolean;
  verifyBannerDismissed: boolean;
  openVerifyDialog: () => void;
  closeVerifyDialog: () => void;
  dismissVerifyBanner: () => void;
  setEmailVerified: (verified: boolean) => void;
  /** Email the current code was sent to while the user is changing their email (account email switches on verify). */
  pendingEmail: string | null;
  setPendingEmail: (email: string | null) => void;
  changeEmailDialogOpen: boolean;
  openChangeEmailDialog: () => void;
  closeChangeEmailDialog: () => void;
  /** After verify-email: the server's user (email may have changed) plus a fresh JWT (it carries the email). */
  applyVerifiedUser: (patch: { email: string; emailVerified: boolean }, token: string) => void;
  /** FE-44: updates user state and localStorage (no-op for guests). */
  applyProfile: (patch: { email: string; fullName: string; role: 'USER' | 'ADMIN'; emailVerified?: boolean }) => void;
  setAuth: (user: User, token: string) => void;
  setGuestMode: () => void;
  logout: () => void;
}

const STORAGE_KEY_TOKEN = 'fretboard_token';
const STORAGE_KEY_USER = 'fretboard_user';
const STORAGE_KEY_GUEST = 'fretboard_is_guest';
const STORAGE_KEY_LAST_USER = 'fretboard_last_user_id';
const STORAGE_KEY_GUEST_STARTED = 'fretboard_guest_started_at';

export const useAuthStore = create<AuthState>((set) => {
  const hasStorage = typeof localStorage !== 'undefined';
  const savedToken = hasStorage ? localStorage.getItem(STORAGE_KEY_TOKEN) : null;
  const savedUser = hasStorage ? localStorage.getItem(STORAGE_KEY_USER) : null;
  const savedIsGuest = hasStorage && localStorage.getItem(STORAGE_KEY_GUEST) === 'true';

  let parsedUser: User | null = null;
  if (savedUser) {
    try {
      parsedUser = JSON.parse(savedUser);
    } catch {
      parsedUser = null;
    }
  }

  return {
    user: parsedUser,
    token: savedToken,
    isAuthenticated: Boolean(savedToken || (savedIsGuest && parsedUser)),
    isGuest: savedIsGuest,
    isLoading: false,
    verifyDialogOpen: false,
    verifyBannerDismissed: false,
    pendingEmail: null,
    changeEmailDialogOpen: false,

    setPendingEmail: (email) => set({ pendingEmail: email }),
    openChangeEmailDialog: () => set({ changeEmailDialogOpen: true }),
    closeChangeEmailDialog: () => set({ changeEmailDialogOpen: false }),
    applyVerifiedUser: (patch, token) =>
      set((state) => {
        if (!state.user || state.isGuest) return state;
        const user = { ...state.user, email: patch.email, emailVerified: patch.emailVerified };
        localStorage.setItem(STORAGE_KEY_TOKEN, token);
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
        return { user, token, pendingEmail: null };
      }),

    openVerifyDialog: () => set({ verifyDialogOpen: true }),
    closeVerifyDialog: () => set({ verifyDialogOpen: false }),
    dismissVerifyBanner: () => set({ verifyBannerDismissed: true }),
    setEmailVerified: (verified) =>
      set((state) => {
        if (!state.user || state.isGuest) return state;
        const user = { ...state.user, emailVerified: verified };
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
        return { user };
      }),

    setAuth: (user, token) => {
      // FRE-142 D5: signing in from a guest session moves that session's library into the account.
      if (useAuthStore.getState().isGuest) {
        void startGuestClaim(user.id, localStorage.getItem(STORAGE_KEY_GUEST_STARTED));
      }
      localStorage.removeItem(STORAGE_KEY_GUEST_STARTED);
      // Survives reloads and out-of-band logouts (token cleared without the store): whoever was
      // signed in last owns any still-unowned rows, never the account signing in now.
      const lastId = localStorage.getItem(STORAGE_KEY_LAST_USER);
      if (lastId && lastId !== user.id) {
        releaseSessionsTo(lastId);
        releaseRoutinesTo(lastId);
      }
      localStorage.setItem(STORAGE_KEY_LAST_USER, user.id);
      localStorage.setItem(STORAGE_KEY_TOKEN, token);
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
      localStorage.removeItem(STORAGE_KEY_GUEST);
      set({ user, token, isAuthenticated: true, isGuest: false, verifyBannerDismissed: false });
    },

    setGuestMode: () => {
      const guestUser: User = {
        id: 'guest-' + Math.random().toString(36).substring(2, 9),
        email: 'guest@fretboard.local',
        fullName: 'نوازنده مهمان (آفلاین)',
        role: 'USER',
        preferredTheme: 'DARK',
        preferredLocale: 'fa'
      };
      localStorage.removeItem(STORAGE_KEY_LAST_USER); // later unowned rows are the guest's
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(guestUser));
      localStorage.setItem(STORAGE_KEY_GUEST, 'true');
      if (!localStorage.getItem(STORAGE_KEY_GUEST_STARTED)) {
        localStorage.setItem(STORAGE_KEY_GUEST_STARTED, new Date().toISOString());
      }
      set({ user: guestUser, token: null, isAuthenticated: true, isGuest: true });
    },

    applyProfile: (patch) =>
      set((state) => {
        if (!state.user || state.isGuest) return state;
        const user = {
          ...state.user,
          email: patch.email,
          fullName: patch.fullName,
          role: patch.role,
          ...(typeof patch.emailVerified === 'boolean' ? { emailVerified: patch.emailVerified } : {})
        };
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
        return { user };
      }),

    logout: () => {
      const prev = useAuthStore.getState();
      if (!prev.isGuest && prev.token) {
        releaseSessionsTo(prev.user?.id);
        releaseRoutinesTo(prev.user?.id);
      }
      localStorage.removeItem(STORAGE_KEY_LAST_USER);
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.removeItem(STORAGE_KEY_USER);
      localStorage.removeItem(STORAGE_KEY_GUEST);
      localStorage.removeItem(STORAGE_KEY_GUEST_STARTED);
      // FRE-142: stop the upload retry timer (tabUploadSync listens; the store must not import sync modules).
      window.dispatchEvent(new Event('fretboard:auth-logout'));
      set({ user: null, token: null, isAuthenticated: false, isGuest: false, verifyDialogOpen: false, changeEmailDialogOpen: false, pendingEmail: null });
    }
  };
});

/** Shared profile refresh for VerifyEmailGate and AdminModerationPage (FE-44). */
export async function refreshProfile(): Promise<{ role: 'USER' | 'ADMIN'; email: string; fullName: string; emailVerified?: boolean } | null> {
  const { token, isGuest } = useAuthStore.getState();
  if (!token || isGuest || (typeof navigator !== 'undefined' && !navigator.onLine)) {
    return null;
  }
  try {
    const res = await apiClient.get<{
      user: {
        email: string;
        full_name: string;
        role: 'USER' | 'ADMIN';
        email_verified?: boolean;
      };
      pending_email?: string | null;
    }>('/api/auth/me');
    if (res?.user) {
      useAuthStore.getState().applyProfile({
        email: res.user.email,
        fullName: res.user.full_name,
        role: res.user.role,
        emailVerified: res.user.email_verified
      });
      useAuthStore.getState().setPendingEmail(res.pending_email ?? null);
      return {
        role: res.user.role,
        email: res.user.email,
        fullName: res.user.full_name,
        emailVerified: res.user.email_verified
      };
    }
  } catch {
    // 401 is handled by apiClient; other errors return null
  }
  return null;
}

